import React, { useEffect, useState } from "react";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar as CalendarIcon, Clock, ArrowRight, ShieldCheck, Info } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { api } from "../../lib/config";
import { priceForSize, leakRepairFee } from "./Pricing";

const TIME_SLOTS = [
  "08:00", "09:00", "10:00", "11:00",
  "13:00", "14:00", "15:00", "16:00",
];

const DEVICE_SIZES = ['1/2"', '3/4"', '1"', '1.25"', '1.5"', '2"', '2.5"', '3"', '4"', '6"', '8" or larger'];

function formatTime(t) {
  const [h] = t.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:00 ${suffix}`;
}

function getQueryParam(name) {
  const p = new URLSearchParams(window.location.search);
  return p.get(name);
}

export default function Booking() {
  const [date, setDate] = useState();
  const [slot, setSlot] = useState("");
  const [loading, setLoading] = useState(false);

  // Post-payment return state
  const [returnState, setReturnState] = useState(null); // {status:'paid'|'pending'|'failed', appointment}

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service: "Backflow Testing",
    device_size: "",
    device_serial: "",
    is_leaking: "no",
    notes: "",
    referred_by_name: "",
    referred_by_phone: "",
  });

  // Referral credit available to this customer (found by phone)
  const [existingCredit, setExistingCredit] = useState(null); // {amount}

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Check for existing credit when phone has at least 7 digits
  useEffect(() => {
    const digits = (form.phone || "").replace(/\D/g, "");
    if (digits.length < 7) {
      setExistingCredit(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/referrals/check`, { params: { phone: form.phone } });
        setExistingCredit(res.data?.has_credit ? { amount: res.data.amount } : null);
      } catch (_) {
        setExistingCredit(null);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [form.phone]);

  // Handle return from Stripe
  useEffect(() => {
    const payment = getQueryParam("payment");
    const sessionId = getQueryParam("session_id");
    if (payment === "cancelled") {
      toast.error("Payment cancelled. Your slot was not reserved.");
      // Clean url
      const url = new URL(window.location.href);
      url.searchParams.delete("payment");
      window.history.replaceState({}, "", url.toString());
      return;
    }
    if (payment === "success" && sessionId) {
      setReturnState({ status: "checking" });
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const res = await api.get(`/appointments/payment-status/${sessionId}`);
          const d = res.data;
          if (d.payment_status === "paid") {
            setReturnState({ status: "paid", appointment: d.appointment });
            toast.success("Payment confirmed — your appointment is locked in!");
            return;
          }
          if (d.status === "expired") {
            setReturnState({ status: "failed" });
            toast.error("Payment session expired.");
            return;
          }
          if (attempts >= 6) {
            setReturnState({ status: "pending" });
            return;
          }
          setTimeout(poll, 2000);
        } catch (e) {
          if (attempts >= 6) {
            setReturnState({ status: "failed" });
            return;
          }
          setTimeout(poll, 2500);
        }
      };
      poll();
      // Clean url session info after handling
      setTimeout(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete("session_id");
        url.searchParams.delete("payment");
        window.history.replaceState({}, "", url.toString() + "#booking");
      }, 600);
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date || !slot) {
      toast.error("Please pick a date and time slot.");
      return;
    }
    if (!form.name || !form.phone || !form.email || !form.address) {
      toast.error("Please fill in name, phone, email and address.");
      return;
    }
    if (!form.device_size) {
      toast.error("Please select your device size.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        appointment: {
          ...form,
          appointment_date: format(date, "yyyy-MM-dd"),
          appointment_time: slot,
        },
        origin_url: window.location.origin,
      };
      const res = await api.post("/appointments/checkout", payload);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start payment. Please call us directly.");
        setLoading(false);
      }
    } catch (err) {
      toast.error("Could not start payment. Please call us directly.");
      setLoading(false);
    }
  };

  if (returnState) {
    return (
      <section
        id="booking"
        className="bg-slate-900 text-white py-20 lg:py-28"
        data-testid="booking-section"
      >
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="border border-white/20 p-8 lg:p-12 bg-white text-slate-900" data-testid="payment-result">
            {returnState.status === "checking" && (
              <>
                <div className="step-num">CONFIRMING…</div>
                <h2 className="font-display text-4xl font-bold mt-3">Confirming your payment…</h2>
                <p className="text-slate-600 mt-3">Hang tight — this takes just a second.</p>
              </>
            )}
            {returnState.status === "paid" && (
              <>
                <div className="step-num !text-green-600">PAID · CONFIRMED</div>
                <h2 className="font-display text-4xl font-bold mt-3">Your slot is locked in.</h2>
                <p className="text-slate-600 mt-3">
                  Thanks {returnState.appointment?.name || ""}! We got your $60 deposit. We'll see you on{" "}
                  <strong>
                    {returnState.appointment?.appointment_date}{" "}
                    {returnState.appointment?.appointment_time}
                  </strong>
                  {" "}for your {returnState.appointment?.service}. A confirmation was emailed to our office — we'll call to confirm within business hours.
                </p>
                <button
                  type="button"
                  onClick={() => setReturnState(null)}
                  className="btn-secondary mt-6"
                  data-testid="book-another-btn"
                >
                  Book Another <ArrowRight className="w-4 h-4" />
                </button>
              </>
            )}
            {returnState.status === "pending" && (
              <>
                <div className="step-num">PROCESSING</div>
                <h2 className="font-display text-4xl font-bold mt-3">Still processing…</h2>
                <p className="text-slate-600 mt-3">
                  Your payment is still being processed. Check your email for a receipt, or call us at 956-942-5496.
                </p>
                <button
                  type="button"
                  onClick={() => setReturnState(null)}
                  className="btn-ghost mt-6"
                >
                  Back to Booking
                </button>
              </>
            )}
            {returnState.status === "failed" && (
              <>
                <div className="step-num !text-red-600">PAYMENT FAILED</div>
                <h2 className="font-display text-4xl font-bold mt-3">Payment couldn't be confirmed.</h2>
                <p className="text-slate-600 mt-3">
                  Nothing was charged. Try again or call us at 956-942-5496.
                </p>
                <button
                  type="button"
                  onClick={() => setReturnState(null)}
                  className="btn-secondary mt-6"
                >
                  Try Again
                </button>
              </>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="bg-slate-900 text-white py-20 lg:py-28" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-12">
        <div>
          <div className="step-num !text-orange-400">04 — SCHEDULE</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-3 tracking-tight">
            Lock in your slot with a <span className="text-orange-400">$60 deposit</span>.
          </h2>
          <p className="text-slate-300 mt-4 max-w-md">
            The $60 deposit reserves your appointment and credits toward your service. Same-day slots
            are available — just call {" "}
            <a href="tel:+19569425496" className="text-orange-400 underline">956-942-5496</a>.
          </p>

          <div className="mt-8 border border-white/20 p-6">
            <div className="overline !text-white/70">Business Hours</div>
            <div className="mt-3 font-display text-lg">Mon – Fri · 8:00 AM – 6:00 PM</div>
            <div className="font-display text-lg">Sat · 9:00 AM – 2:00 PM</div>
            <div className="text-slate-400 text-sm mt-1">Sun · Emergency only</div>
          </div>

          <div className="mt-6 border border-orange-400/60 bg-orange-500/10 p-5 text-sm text-orange-50">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 text-orange-300" />
              <div>
                <div className="font-display font-bold text-orange-300">Cancellation Policy</div>
                The $60 deposit is <strong>non-refundable</strong> on cancellation or no-show.
                Rescheduling is free if you give us 24 hours notice.
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white text-slate-900 p-6 lg:p-8 border border-white/20"
          data-testid="appointment-form"
        >
          <div className="flex items-center gap-2 text-blue-600">
            <ShieldCheck className="w-4 h-4" />
            <span className="overline !text-blue-600">Secure · Stripe Checkout</span>
          </div>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Book Your Test</h3>

          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <div>
              <label className="field-label">Name *</label>
              <input name="name" value={form.name} onChange={onChange} className="lb-input" required data-testid="appt-name-input" />
            </div>
            <div>
              <label className="field-label">Phone *</label>
              <input name="phone" type="tel" value={form.phone} onChange={onChange} className="lb-input" required data-testid="appt-phone-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Email *</label>
              <input name="email" type="email" value={form.email} onChange={onChange} className="lb-input" required data-testid="appt-email-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Service Address *</label>
              <input name="address" value={form.address} onChange={onChange} className="lb-input" required data-testid="appt-address-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Service</label>
              <select name="service" value={form.service} onChange={onChange} className="lb-select" data-testid="appt-service-select">
                <option>Backflow Testing</option>
                <option>Backflow Installation</option>
                <option>Backflow Repair</option>
                <option>City of Laredo Registration</option>
                <option>Annual Re-certification</option>
              </select>
            </div>

            {/* Device details */}
            <div>
              <label className="field-label">Device Size *</label>
              <select name="device_size" value={form.device_size} onChange={onChange} className="lb-select" required data-testid="appt-device-size">
                <option value="">Select size</option>
                {DEVICE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                {form.device_size && priceForSize(form.device_size)
                  ? <>Service price: <strong className="text-slate-900">${priceForSize(form.device_size)} + tax</strong> · $60 deposit credits toward total.</>
                  : "Size determines final service price."}
              </p>
            </div>
            <div>
              <label className="field-label">Device Serial #</label>
              <input name="device_serial" value={form.device_serial} onChange={onChange} className="lb-input" placeholder="On the brass tag" data-testid="appt-device-serial" />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Is the device currently leaking? *</label>
              <div className="flex flex-wrap gap-2" data-testid="appt-leaking-group">
                {[
                  { v: "no", l: "No" },
                  { v: "yes", l: "Yes" },
                  { v: "unsure", l: "Not sure" },
                ].map((o) => (
                  <label
                    key={o.v}
                    className={`cursor-pointer px-4 py-2 border font-display font-semibold text-sm transition-colors ${
                      form.is_leaking === o.v
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-900 border-slate-300 hover:border-slate-900"
                    }`}
                    data-testid={`appt-leaking-${o.v}`}
                  >
                    <input
                      type="radio"
                      name="is_leaking"
                      value={o.v}
                      checked={form.is_leaking === o.v}
                      onChange={onChange}
                      className="sr-only"
                    />
                    {o.l}
                  </label>
                ))}
              </div>
            </div>

            {/* Scheduling */}
            <div>
              <label className="field-label">Preferred Date *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="appt-date-trigger"
                    className="lb-input flex items-center justify-between w-full text-left"
                  >
                    {date ? format(date, "PPP") : <span className="text-slate-400">Select date</span>}
                    <CalendarIcon className="w-4 h-4 text-slate-500" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 border border-slate-200 rounded-none" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="field-label">Time Slot *</label>
              <select
                value={slot}
                onChange={(e) => setSlot(e.target.value)}
                className="lb-select"
                data-testid="appt-time-select"
              >
                <option value="">Select time</option>
                {TIME_SLOTS.map((t) => (
                  <option key={t} value={t}>
                    {formatTime(t)}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Notes</label>
              <textarea name="notes" value={form.notes} onChange={onChange} rows={3} className="lb-textarea" data-testid="appt-notes-input" />
            </div>

            {/* Referral program */}
            <div className="sm:col-span-2 border-t border-slate-200 pt-5 mt-2">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="overline !text-orange-600">REFER A NEIGHBOR · $25 OFF THEIR NEXT ANNUAL</div>
                  <p className="text-sm text-slate-600 mt-2 max-w-md">
                    Know a neighbor who got the same backflow notice? Drop their name + number below.
                    When they book their annual, we'll apply <strong>$25 off your next test</strong> automatically.
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="field-label">Referred by — name</label>
                  <input
                    name="referred_by_name"
                    value={form.referred_by_name}
                    onChange={onChange}
                    className="lb-input"
                    placeholder="Neighbor's name (optional)"
                    data-testid="appt-referred-by-name"
                  />
                </div>
                <div>
                  <label className="field-label">Referred by — phone</label>
                  <input
                    name="referred_by_phone"
                    value={form.referred_by_phone}
                    onChange={onChange}
                    className="lb-input"
                    placeholder="Their phone (optional)"
                    data-testid="appt-referred-by-phone"
                  />
                </div>
              </div>

              {existingCredit && (
                <div
                  className="mt-4 border border-green-500 bg-green-50 text-green-800 p-3 text-sm flex items-center gap-2"
                  data-testid="existing-credit-banner"
                >
                  <ShieldCheck className="w-4 h-4 text-green-600" />
                  <span>
                    <strong>${existingCredit.amount.toFixed(0)} referral credit</strong> found for this phone — it'll be applied to your balance.
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Live price estimate */}
          {form.device_size && priceForSize(form.device_size) && (
            <div
              className="mt-6 border border-slate-200 bg-slate-50 p-5"
              data-testid="price-estimate"
            >
              <div className="overline">Your Estimate</div>
              <div className="mt-2 space-y-1 text-sm text-slate-700">
                {(() => {
                  const test = priceForSize(form.device_size);
                  const leak = form.is_leaking === "yes" ? leakRepairFee(form.device_size) : 0;
                  const svcTotal = test + leak;
                  const credit = existingCredit ? existingCredit.amount : 0;
                  const balance = Math.max(0, svcTotal - 60 - credit);
                  return (
                    <>
                      <div className="flex justify-between">
                        <span>Certified test ({form.device_size})</span>
                        <span className="font-display font-semibold">${test}</span>
                      </div>
                      {leak > 0 && (
                        <div className="flex justify-between text-orange-700">
                          <span>Leak repair (flat rate)</span>
                          <span className="font-display font-semibold">+ ${leak}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t border-slate-300 pt-2 mt-2">
                        <span className="font-display font-bold text-slate-900">Service total</span>
                        <span className="font-display font-bold text-slate-900">
                          ${svcTotal}<span className="text-xs font-normal text-slate-500"> + tax</span>
                        </span>
                      </div>
                      <div className="flex justify-between text-slate-500 text-xs pt-1">
                        <span>Less $60 deposit (paid now)</span>
                        <span>-$60</span>
                      </div>
                      {credit > 0 && (
                        <div className="flex justify-between text-green-700 text-xs">
                          <span>Referral credit</span>
                          <span>-${credit.toFixed(0)}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-1">
                        <span className="text-slate-600">Balance due at service</span>
                        <span className="font-display font-semibold text-slate-900">
                          ${balance}<span className="text-xs font-normal text-slate-500"> + tax</span>
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-5">
            <div>
              <div className="overline">Due Now</div>
              <div className="font-display text-3xl font-bold">$60.00 <span className="text-sm font-normal text-slate-500">deposit</span></div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary disabled:opacity-60"
              data-testid="appt-submit-btn"
            >
              {loading ? "Redirecting…" : "Pay $60 & Book"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <p className="text-xs text-slate-500 mt-3 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" /> Secure Stripe checkout · Non-refundable on cancellation.
          </p>
        </form>
      </div>
    </section>
  );
}
